import { Button, IconButton, Spacer, SvgClose } from '@loomhq/lens';
import { textReplace } from '@loomhq/loom-embed';
import Medium from 'medium.js';
import Mousetrap from 'mousetrap';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { updateAddCommentError, updateInGuestFlow } from 'actions/comments';
import { updateWebRecordingUtilityRecordedVideo } from 'actions/webRecordingUtility';
import {
  VIDEO_DESCRIPTION_ADD_LINK_CLICK,
  WRU_REPLY_PREVIEW_CLICKED,
  WRU_REPLY_PREVIEW_GO_TO_VIDEO_CLICKED,
  WRU_REPLY_REMOVE_REPLY_CLICKED,
} from 'constants/events';
import { COMMAND_ENTER, CTRL_ENTER } from 'constants/mousetrap';
import { addCommentCreator } from 'creators/comments';
import * as analytics from 'utilities/analytics';
import { addMention, calculateInlineEmbedDimensions } from 'utilities/comments';
import { stripContent } from 'utilities/mediumHelpers';
import * as sentry from 'utilities/sentry';

import { MAX_COMMENT_LENGTH } from 'validate';

import CustomTimeHandler from './custom-time-handler';
import GuestFlow from '../guest-flow';
import AddUrlToVideo from '../../add-url-to-video';
import CommentMentions from '../../mentions';
import { ROLLOUT_WEBSITE_RECORDING } from '../../../../../../shared/constants/featureFlag';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import * as wru from 'components/web-recording-utility';

import './styles.less';

// This isn't a great general utility but merely one that
// calculates a semi-readable text color for branded button background
function calculateContrastyColor(color) {
  if (!color) {
    return undefined;
  }

  const hex = color.replace(/#/, '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Counting the perceptive luminance - human eye favors green color...
  const a = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // 0.4 is our magical constant based on tweaks with B and HubSpot's brand orange
  const d = a < 0.4 ? 0 : 255;

  return `rgb(${d}, ${d}, ${d})`;
}

const AddComment = class extends Component {
  state = {
    did_mount: false,
    add_comment_active: false,
    comment_mentions: {},
    show_add_link_bar: false,
    render_add_link_bar: true,
  };

  render() {
    const { did_mount, add_comment_active } = this.state;

    const {
      commentsEnabled,
      errorAddComment,
      isFetching,
      isInGuestFlow,
      currentUserIsOwner,
      companyFeatureData,
      videoReplyUrl,
      wruRecordingInitiated,
    } = this.props;

    const commentsDisabledClass = commentsEnabled ? '' : 'comments-disabled';
    const commentPostEnabled = add_comment_active ? 'is-active' : '';

    const errorAddCommentClass = errorAddComment ? 'error' : '';
    const { brand_primary_color: brandColor } = companyFeatureData || {};

    const constrastBrandColor = calculateContrastyColor(brandColor);

    const buttonStyle =
      brandColor && !currentUserIsOwner
        ? {
            borderColor: brandColor,
            backgroundColor: brandColor,
            color: constrastBrandColor,
          }
        : null;
    const textStyle = buttonStyle ? { color: brandColor } : null;
    const errBorderStyle =
      errorAddCommentClass === 'error' && textStyle
        ? { border: `1px solid ${brandColor}` }
        : null;

    const addUrlToVideoHiddenClass = this.state.show_add_link_bar
      ? ''
      : 'hidden';

    const postButtonIsDisabled =
      isFetching || !commentPostEnabled || !commentsEnabled;

    return (
      <React.Fragment>
        {this.props.showAddLinkBar && (
          <div className={`add-url-to-video-root ${addUrlToVideoHiddenClass}`}>
            <AddUrlToVideo
              onClickAdd={this.handleAddLinkToDescription}
              onClickClose={this.handleCloseAddLinkBar}
            />
          </div>
        )}
        <div
          style={errBorderStyle}
          className={`add-comment ${commentsDisabledClass} ${errorAddCommentClass} ${
            isInGuestFlow ? 'hidden-content' : ''
          }`}
        >
          {isInGuestFlow && (
            <GuestFlow
              buttonStyle={buttonStyle}
              addComment={this.addComment}
              resetNoGuestFlow={this.resetGuestFlow}
            />
          )}
          <div
            className="add-comment-content"
            ref={addCommentContent =>
              (this.addCommentContent = addCommentContent)
            }
            onInput={this.validateComment}
          />
          <div
            className={`add-comment-actions ${isInGuestFlow ? 'hidden' : ''}`}
          >
            <div
              className="add-comments-click-through-area"
              onClick={this.handleCommentClickThroughAreaClick}
            />
            <div className="save-and-time">
              {errorAddComment && (
                <span className="validation-error-react" style={textStyle}>
                  {errorAddComment}
                </span>
              )}
              {!errorAddComment && <CustomTimeHandler />}
            </div>
          </div>
          {videoReplyUrl && (
            <div className="add-comment-content-link-preview">
              <div
                className="add-comment-content-link-preview-placeholder"
                ref={me => (this.addCommentLinkPreviewDiv = me)}
                onClick={() => analytics.track(WRU_REPLY_PREVIEW_CLICKED)}
              />
              <IconButton
                altText="Cancel"
                onClick={e => {
                  e.stopPropagation();
                  this.removeInlineCommentReplyVideo();
                  analytics.track(WRU_REPLY_REMOVE_REPLY_CLICKED);
                }}
                iconColor="grey5"
                icon={<SvgClose />}
                className="add-comment-content-link-preview-cross-icon"
              />
              <Spacer top={2} />
              <a
                href={videoReplyUrl}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex="0"
                onClick={e => {
                  e.stopPropagation(); // do not trigger parent onclick handler
                  analytics.track(WRU_REPLY_PREVIEW_GO_TO_VIDEO_CLICKED);

                  return true;
                }}
              >
                <Button>Go to Video</Button>
              </a>
            </div>
          )}
          {did_mount && (
            <CommentMentions
              onMentionClick={this.addMention}
              listeneeClass=".add-comment-content"
            />
          )}
        </div>
        <div className="add-comment-buttons">
          <FeatureFlaggedComponent
            flag={ROLLOUT_WEBSITE_RECORDING}
            activationValues={[true]}
          >
            {!videoReplyUrl && (
              <wru.RecordButton
                isVideoReply={true}
                label="Record a Reply"
                isDisabled={wruRecordingInitiated || !commentsEnabled}
                location="add-comment"
              />
            )}
            {videoReplyUrl && (
              <Button
                icon={<SvgClose />}
                onClick={() => {
                  this.removeInlineCommentReplyVideo();
                  analytics.track(WRU_REPLY_REMOVE_REPLY_CLICKED);
                }}
              >
                Remove Reply
              </Button>
            )}
          </FeatureFlaggedComponent>
          <Spacer right={2} />
          <Button
            variant="primary"
            isDisabled={postButtonIsDisabled}
            onClick={this.addComment}
            style={
              postButtonIsDisabled
                ? null
                : {
                    backgroundColor:
                      'var(--brandThemedPrimaryButtonBackgroundColor, var(--lns-color-primary))',
                  }
            }
          >
            Post
          </Button>
        </div>
      </React.Fragment>
    );
  }

  handleCommentClickThroughAreaClick = () => {
    if (!this.props.commentsEnabled) {
      return;
    }

    this.addCommentContent.focus();
  };

  handleAddLinkToDescription = () => {
    const {
      id,
      video_properties: { from_url = null },
    } = this.props.video;

    if (from_url == null) {
      return;
    }

    analytics.track(VIDEO_DESCRIPTION_ADD_LINK_CLICK, {
      has_content: this.stripMediumContent().trim().length > 0,
      video_id: id,
    });

    const insertHtml = `<div>${from_url}</div>`;

    this.medium.value(this.medium.value() + insertHtml);
    this.validateComment();

    this.setState({ show_add_link_bar: false });
  };

  handleCloseAddLinkBar = userInitiated => {
    // if we detect there's no need for add link bar, then
    // we hide it immediately (ie. avoid rendering it entirely)
    // whereas if user hides it, it should just nicely collapse with an animation
    this.setState({
      show_add_link_bar: false,
      render_add_link_bar: userInitiated,
    });
  };

  addComment = () => {
    const { isInGuestFlow, isLoggedIn, commentsEnabled } = this.props;

    if (!commentsEnabled) {
      return;
    }

    const strippedMediumContent = this.stripMediumContent();

    if (strippedMediumContent.length === 0) {
      this.props.updateAddCommentError('Cannot be empty');

      return;
    }

    if (!isLoggedIn && !isInGuestFlow) {
      return this.props.updateInGuestFlow(true);
    }

    this.removeInlineCommentReplyVideo();

    this.props.addComment(
      strippedMediumContent,
      this.medium,
      this.state.comment_mentions
    );
    this.props.updateInGuestFlow(false);
    this.setState({ comment_mentions: {} });
  };

  stripMediumContent = () => {
    if (this.medium == null) {
      return;
    }

    return stripContent(this.medium);
  };

  resetGuestFlow = () => {
    this.props.updateInGuestFlow(false);
  };

  componentDidMount() {
    this.medium = new Medium({
      attributes: null,
      autofocus: false,
      autoHR: false,
      element: this.addCommentContent,
      mode: Medium.richMode,
      maxLength: MAX_COMMENT_LENGTH,
      pasteAsText: false,
      placeholder: 'Write a comment',
      tags: null,
    });

    this.addMousetrapBindings();
    this.setState({ did_mount: true });

    const { currentUserIsOwner, video, videoIsTeamShared } = this.props;

    const strippedMediumContent = this.stripMediumContent();

    if (
      (currentUserIsOwner ||
        videoIsTeamShared ||
        video.collaboratePermissions.write) &&
      video != null &&
      strippedMediumContent.length === 0
    ) {
      this.setState({ show_add_link_bar: true });
    }
  }

  componentDidUpdate(prevProps) {
    // will automatically be called after mount due to the setState in compDidMount()
    this.medium.settings.maxLength = this.props.commentsEnabled
      ? MAX_COMMENT_LENGTH
      : 0;

    const { videoReplyUrl } = this.props;

    if (videoReplyUrl && !prevProps.videoReplyUrl) {
      this.populateInlineCommentReplyVideo(videoReplyUrl);
    } else if (prevProps.videoReplyUrl && !videoReplyUrl) {
      this.removeInlineCommentReplyVideo();
    }
  }

  populateInlineCommentReplyVideo = async videoUrl => {
    this.addCommentContent.innerText =
      this.addCommentContent.innerText + videoUrl;
    this.linkPreviewHtml = null;

    try {
      if (textReplace) {
        this.linkPreviewHtml = await textReplace(
          videoUrl,
          calculateInlineEmbedDimensions(this.addCommentContent.offsetWidth)
        );
      }
    } catch (err) {
      sentry.logException(err, 'Error while populating link preview', {
        videoUrl,
      });
    }

    this.addCommentLinkPreviewDiv.innerHTML =
      this.linkPreviewHtml + this.addCommentLinkPreviewDiv.innerHTML;
    this.validateComment();
  };

  removeInlineCommentReplyVideo = () => {
    this.addCommentContent.innerText = this.addCommentContent.innerText.replace(
      this.props.videoReplyUrl,
      ''
    );

    this.props.updateWebRecordingUtilityRecordedVideo(null);
    this.validateComment();
  };

  validateComment = () => {
    this.setState({
      add_comment_active:
        this.addCommentContent && this.addCommentContent.innerText !== '',
    });

    this.props.updateAddCommentError(null);

    // Fix for Medium not updating when content is inserted via emoji picker on macos
    this.medium.html.placeholders();
  };

  addMousetrapBindings = () => {
    Mousetrap(this.addCommentContent).bind([CTRL_ENTER, COMMAND_ENTER], () => {
      this.addComment();
    });
  };

  addMention = ({ result, query }, trigger) => {
    this.setState({
      comment_mentions: addMention(
        result,
        query,
        '.add-comment-content',
        this.state.comment_mentions,
        trigger
      ),
    });
  };
};

const mapStateToProps = state => {
  const {
    current_user_is_owner: currentUserIsOwner,
    url_for_display,
    video,
    video_is_team_shared: videoIsTeamShared,
  } = state.share_video;

  const {
    comments_enabled: commentsEnabled,
    company_feature_data: companyFeatureData,
  } = video;

  const {
    error_add_comment: errorAddComment,
    is_fetching: isFetching,
    in_guest_flow: isInGuestFlow,
    comments,
  } = state.comments;

  const { is_logged_in: isLoggedIn } = state.user;

  const noCommentsWithLink = comments.every(
    c => c.content.indexOf(url_for_display) === -1
  );
  const hasFromUrl = Boolean(video.video_properties.from_url);

  const {
    videoUrl: videoReplyUrl,
    recordingInitiated: wruRecordingInitiated,
  } = state.webRecordingUtility;

  return {
    commentsEnabled,
    showAddLinkBar:
      !isFetching &&
      (currentUserIsOwner ||
        videoIsTeamShared ||
        video.collaboratePermissions.write) &&
      noCommentsWithLink &&
      commentsEnabled &&
      hasFromUrl,
    errorAddComment,
    isFetching,
    isInGuestFlow,
    isLoggedIn,
    companyFeatureData,
    currentUserIsOwner,
    video,
    videoIsTeamShared,
    videoReplyUrl,
    wruRecordingInitiated,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    addComment: (content, medium, mentions) =>
      dispatch(addCommentCreator(content, medium, mentions)),
    updateInGuestFlow: inGuestFlow => dispatch(updateInGuestFlow(inGuestFlow)),
    updateAddCommentError: err => dispatch(updateAddCommentError(err)),
    updateWebRecordingUtilityRecordedVideo: url =>
      dispatch(updateWebRecordingUtilityRecordedVideo(url)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddComment);
